import { BaseResource } from './base';
import { Grade, ListResponse, PaginationParams } from '../types';

export interface CreateGradeData {
    studentId: string;
    courseId: string;
    assignmentName: string;
    score: number;
    maxScore: number;
    weight: number;
    category: 'homework' | 'quiz' | 'exam' | 'project' | 'participation';
    gradedDate?: string;
    notes?: string;
}

export interface UpdateGradeData extends Partial<Omit<CreateGradeData, 'studentId' | 'courseId'>> { }

export interface GradeFilters extends PaginationParams {
    studentId?: string;
    courseId?: string;
    category?: 'homework' | 'quiz' | 'exam' | 'project' | 'participation';
    gradedDateFrom?: string;
    gradedDateTo?: string;
    minScore?: number;
    maxScore?: number;
}

export class GradesResource extends BaseResource {
    /**
     * List all grades with optional filtering and pagination
     */
    async list(filters?: GradeFilters): Promise<ListResponse<Grade>> {
        return super.requestGet<ListResponse<Grade>>('/grades', filters);
    }

    /**
     * Get a specific grade by ID
     */
    async get(gradeId: string): Promise<Grade> {
        return super.requestGet<Grade>(`/grades/${gradeId}`);
    }

    /**
     * Create a new grade
     */
    async create(data: CreateGradeData): Promise<Grade> {
        return super.requestPost<Grade>('/grades', data);
    }

    /**
     * Update an existing grade
     */
    async update(gradeId: string, data: UpdateGradeData): Promise<Grade> {
        return super.requestPatch<Grade>(`/grades/${gradeId}`, data);
    }

    /**
     * Delete a grade
     */
    async delete(gradeId: string): Promise<{ success: boolean; message: string }> {
        return super.requestDelete(`/grades/${gradeId}`);
    }

    /**
     * Bulk create grades for multiple students
     */
    async bulkCreate(grades: CreateGradeData[]): Promise<{ created: Grade[]; errors: any[] }> {
        return super.requestPost('/grades/bulk', { grades });
    }

    /**
     * Get grade statistics for a course
     */
    async getCourseStats(courseId: string, filters?: { category?: string }): Promise<{
        average: number;
        median: number;
        highest: number;
        lowest: number;
        distribution: Record<string, number>;
    }> {
        return super.requestGet(`/grades/stats/course/${courseId}`, filters);
    }

    /**
     * Get grade statistics for a student
     */
    async getStudentStats(studentId: string, filters?: { courseId?: string; semester?: string }): Promise<{
        gpa: number;
        totalCredits: number;
        courseGrades: Array<{
            courseId: string;
            courseName: string;
            grade: string;
            credits: number;
        }>;
    }> {
        return super.requestGet(`/grades/stats/student/${studentId}`, filters);
    }
}
